import { useAsyncData, useRoute } from '#imports'
import { computed } from 'vue'

import { getAsyncDataKey } from '@backmarket/nuxt-module-http-v2/getAsyncDataKey'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import { uniqBy } from '@backmarket/utils/collection/uniqBy'
import { toBcp47Locale } from '@backmarket/utils/string/toBcp47Locale'

import { useMasterPP } from '~/scopes/product-core/composables/useMasterPP/useMasterPP'
import { useLinkBooster } from '~/scopes/seo/composables/useLinkBooster'

export function useProductPageSeoLinks(sourceUrl: string) {
  const $httpFetch = useHttpFetch()
  const route = useRoute()
  const { isMasterPPUrl } = useMasterPP()
  const locale = useI18nLocale()
  const { fetchLinkBoosters } = useLinkBooster()

  const { data: linkBoosters } = useAsyncData(
    getAsyncDataKey('product-link-booster', {
      path: route.path,
    }),
    async () =>
      fetchLinkBoosters({
        sourceUrl,
        listType: 'pp_linkboosters',
        timeout: 500,
      }),
    {
      lazy: true,
      default: () => [],
      watch: [() => route.path],
    },
  )

  const { data: siblings } = useAsyncData(
    getAsyncDataKey('siblings', { slug: route.params.slug || 'no-slug' }),
    async () => {
      if (!isMasterPPUrl.value) {
        return []
      }

      const data = await $httpFetch(
        '/product-page/parent-products/siblings/:slug',
        {
          pathParams: { slug: `${route.params.slug}` },
          timeout: 500,
        },
      )

      return (data || []).map(({ slug, title }) => ({
        title,
        relativeLink: `/${toBcp47Locale(locale)}/p/${slug}`,
      }))
    },
    {
      lazy: true,
      default: () => [],
      watch: [() => route.params.slug],
    },
  )

  return computed(() =>
    uniqBy([...siblings.value, ...linkBoosters.value], 'title'),
  )
}
